import React, { useCallback, useEffect, useState } from 'react';
import {
  FaEnvelope,
  FaMobileAlt,
  FaBolt,
  FaCalendarAlt,
  FaClock,
  FaTrophy,
  FaChartLine,
  FaComments,
  FaNewspaper,
  FaCheckCircle,
  FaTasks,
  FaUsers,
} from 'react-icons/fa';

export type NotifFrequency = 'instant' | 'digest' | 'weekly';

export type NotificationPrefs = {
  email: {
    newTasks: boolean;
    solutionResults: boolean;
    weeklyDigest: boolean;
    platformNews: boolean;
  };
  push: {
    mentions: boolean;
    achievements: boolean;
    leaderboard: boolean;
    classUpdates: boolean;
  };
  frequency: NotifFrequency;
  quietFrom: string;
  quietTo: string;
};

const STORAGE_PREFIX = 'sciplex_notification_prefs_';

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  email: {
    newTasks: true,
    solutionResults: true,
    weeklyDigest: true,
    platformNews: false,
  },
  push: {
    mentions: true,
    achievements: true,
    leaderboard: false,
    classUpdates: true,
  },
  frequency: 'instant',
  quietFrom: '22:00',
  quietTo: '08:00',
};

export function loadNotificationPrefs(userId: number): NotificationPrefs {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
    if (!raw) return { ...DEFAULT_NOTIFICATION_PREFS, email: { ...DEFAULT_NOTIFICATION_PREFS.email }, push: { ...DEFAULT_NOTIFICATION_PREFS.push } };
    const parsed = JSON.parse(raw) as Partial<NotificationPrefs>;
    return {
      email: { ...DEFAULT_NOTIFICATION_PREFS.email, ...(parsed.email || {}) },
      push: { ...DEFAULT_NOTIFICATION_PREFS.push, ...(parsed.push || {}) },
      frequency: parsed.frequency === 'digest' || parsed.frequency === 'weekly' ? parsed.frequency : 'instant',
      quietFrom: typeof parsed.quietFrom === 'string' ? parsed.quietFrom : DEFAULT_NOTIFICATION_PREFS.quietFrom,
      quietTo: typeof parsed.quietTo === 'string' ? parsed.quietTo : DEFAULT_NOTIFICATION_PREFS.quietTo,
    };
  } catch {
    return { ...DEFAULT_NOTIFICATION_PREFS, email: { ...DEFAULT_NOTIFICATION_PREFS.email }, push: { ...DEFAULT_NOTIFICATION_PREFS.push } };
  }
}

export function saveNotificationPrefs(userId: number, prefs: NotificationPrefs) {
  localStorage.setItem(`${STORAGE_PREFIX}${userId}`, JSON.stringify(prefs));
}

type ToggleRow = {
  key: string;
  icon: React.ReactNode;
  titleKey: string;
  descKey: string;
};

type Props = {
  userId: number;
  t: (key: string) => string;
};

function ToggleSwitch({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      className={`settings-notif-toggle${checked ? ' is-on' : ''}`}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
    >
      <span className="settings-notif-toggle__knob" />
    </button>
  );
}

export function NotificationSettings({ userId, t }: Props) {
  const [prefs, setPrefs] = useState<NotificationPrefs>(() => loadNotificationPrefs(userId));
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setPrefs(loadNotificationPrefs(userId));
  }, [userId]);

  const emailRows: ToggleRow[] = [
    { key: 'newTasks', icon: <FaTasks />, titleKey: 'settings.notifEmailTasks', descKey: 'settings.notifEmailTasksDesc' },
    { key: 'solutionResults', icon: <FaCheckCircle />, titleKey: 'settings.notifEmailResults', descKey: 'settings.notifEmailResultsDesc' },
    { key: 'weeklyDigest', icon: <FaNewspaper />, titleKey: 'settings.notifEmailDigest', descKey: 'settings.notifEmailDigestDesc' },
    { key: 'platformNews', icon: <FaEnvelope />, titleKey: 'settings.notifEmailNews', descKey: 'settings.notifEmailNewsDesc' },
  ];

  const pushRows: ToggleRow[] = [
    { key: 'mentions', icon: <FaComments />, titleKey: 'settings.notifPushMentions', descKey: 'settings.notifPushMentionsDesc' },
    { key: 'achievements', icon: <FaTrophy />, titleKey: 'settings.notifPushAchievements', descKey: 'settings.notifPushAchievementsDesc' },
    { key: 'leaderboard', icon: <FaChartLine />, titleKey: 'settings.notifPushLeaderboard', descKey: 'settings.notifPushLeaderboardDesc' },
    { key: 'classUpdates', icon: <FaUsers />, titleKey: 'settings.notifPushClasses', descKey: 'settings.notifPushClassesDesc' },
  ];

  const setEmail = (key: keyof NotificationPrefs['email'], value: boolean) => {
    setPrefs((p) => ({ ...p, email: { ...p.email, [key]: value } }));
  };

  const setPush = (key: keyof NotificationPrefs['push'], value: boolean) => {
    setPrefs((p) => ({ ...p, push: { ...p.push, [key]: value } }));
  };

  const emailAllOff = Object.values(prefs.email).every((v) => !v);
  const pushAllOff = Object.values(prefs.push).every((v) => !v);

  const disableAllEmail = () => {
    setPrefs((p) => ({
      ...p,
      email: { newTasks: false, solutionResults: false, weeklyDigest: false, platformNews: false },
    }));
  };

  const disableAllPush = () => {
    setPrefs((p) => ({
      ...p,
      push: { mentions: false, achievements: false, leaderboard: false, classUpdates: false },
    }));
  };

  const disableEverything = () => {
    setPrefs({
      ...prefs,
      email: { newTasks: false, solutionResults: false, weeklyDigest: false, platformNews: false },
      push: { mentions: false, achievements: false, leaderboard: false, classUpdates: false },
    });
  };

  const handleSave = useCallback(() => {
    saveNotificationPrefs(userId, prefs);
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2000);
  }, [userId, prefs]);

  return (
    <div className="settings-container settings-notif">
      <h1 className="settings-title">{t('settings.sectionNotifications')}</h1>
      <p className="settings-subtitle">{t('settings.notifIntro')}</p>
      {savedFlash && <div className="alert alert-success">{t('settings.notifSaved')}</div>}

      <section className="settings-notif-card">
        <div className="settings-notif-card__head">
          <div className="settings-notif-card__head-main">
            <span className="settings-notif-card__icon" aria-hidden>
              <FaEnvelope />
            </span>
            <div>
              <h2 className="settings-notif-card__title">{t('settings.notifEmailTitle')}</h2>
              <p className="settings-notif-card__desc">{t('settings.notifEmailDesc')}</p>
            </div>
          </div>
          <button
            type="button"
            className={`settings-notif-disable-all${emailAllOff ? ' is-disabled' : ''}`}
            onClick={disableAllEmail}
            disabled={emailAllOff}
          >
            {t('settings.notifDisableAll')}
          </button>
        </div>
        <ul className="settings-notif-list">
          {emailRows.map((row) => (
            <li key={row.key} className="settings-notif-row">
              <span className="settings-notif-row__icon" aria-hidden>
                {row.icon}
              </span>
              <div className="settings-notif-row__text">
                <span className="settings-notif-row__title">{t(row.titleKey)}</span>
                <span className="settings-notif-row__desc">{t(row.descKey)}</span>
              </div>
              <ToggleSwitch
                checked={prefs.email[row.key as keyof NotificationPrefs['email']]}
                onChange={(v) => setEmail(row.key as keyof NotificationPrefs['email'], v)}
                ariaLabel={t(row.titleKey)}
              />
            </li>
          ))}
        </ul>
      </section>

      <section className="settings-notif-card">
        <div className="settings-notif-card__head">
          <div className="settings-notif-card__head-main">
            <span className="settings-notif-card__icon" aria-hidden>
              <FaMobileAlt />
            </span>
            <div>
              <h2 className="settings-notif-card__title">{t('settings.notifPushTitle')}</h2>
              <p className="settings-notif-card__desc">{t('settings.notifPushDesc')}</p>
            </div>
          </div>
          <button
            type="button"
            className={`settings-notif-disable-all${pushAllOff ? ' is-disabled' : ''}`}
            onClick={disableAllPush}
            disabled={pushAllOff}
          >
            {t('settings.notifDisableAll')}
          </button>
        </div>
        <ul className="settings-notif-list">
          {pushRows.map((row) => (
            <li key={row.key} className="settings-notif-row">
              <span className="settings-notif-row__icon" aria-hidden>
                {row.icon}
              </span>
              <div className="settings-notif-row__text">
                <span className="settings-notif-row__title">{t(row.titleKey)}</span>
                <span className="settings-notif-row__desc">{t(row.descKey)}</span>
              </div>
              <ToggleSwitch
                checked={prefs.push[row.key as keyof NotificationPrefs['push']]}
                onChange={(v) => setPush(row.key as keyof NotificationPrefs['push'], v)}
                ariaLabel={t(row.titleKey)}
              />
            </li>
          ))}
        </ul>
      </section>

      <section className="settings-notif-block">
        <h2 className="settings-notif-block__title">{t('settings.notifFrequencyTitle')}</h2>
        <p className="settings-notif-block__desc">{t('settings.notifFrequencyDesc')}</p>
        <div className="settings-notif-freq">
          {(
            [
              { id: 'instant' as const, icon: <FaBolt />, titleKey: 'settings.notifFreqInstant', descKey: 'settings.notifFreqInstantDesc' },
              { id: 'digest' as const, icon: <FaClock />, titleKey: 'settings.notifFreqDigest', descKey: 'settings.notifFreqDigestDesc' },
              { id: 'weekly' as const, icon: <FaCalendarAlt />, titleKey: 'settings.notifFreqWeekly', descKey: 'settings.notifFreqWeeklyDesc' },
            ]
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`settings-notif-freq-card${prefs.frequency === opt.id ? ' is-selected' : ''}`}
              onClick={() => setPrefs((p) => ({ ...p, frequency: opt.id }))}
            >
              <span className="settings-notif-freq-card__radio" aria-hidden />
              <span className="settings-notif-freq-card__icon" aria-hidden>
                {opt.icon}
              </span>
              <span className="settings-notif-freq-card__title">{t(opt.titleKey)}</span>
              <span className="settings-notif-freq-card__desc">{t(opt.descKey)}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="settings-notif-block">
        <h2 className="settings-notif-block__title">{t('settings.notifQuietTitle')}</h2>
        <p className="settings-notif-block__desc">{t('settings.notifQuietDesc')}</p>
        <div className="settings-notif-quiet">
          <div className="settings-field">
            <label className="settings-field-label" htmlFor="notif-quiet-from">
              {t('settings.notifQuietFrom')}
            </label>
            <input
              id="notif-quiet-from"
              type="time"
              className="settings-input settings-select"
              value={prefs.quietFrom}
              onChange={(e) => setPrefs((p) => ({ ...p, quietFrom: e.target.value }))}
              style={{ colorScheme: 'dark' }}
            />
          </div>
          <div className="settings-field">
            <label className="settings-field-label" htmlFor="notif-quiet-to">
              {t('settings.notifQuietTo')}
            </label>
            <input
              id="notif-quiet-to"
              type="time"
              className="settings-input settings-select"
              value={prefs.quietTo}
              onChange={(e) => setPrefs((p) => ({ ...p, quietTo: e.target.value }))}
              style={{ colorScheme: 'dark' }}
            />
          </div>
        </div>
      </section>

      <div className="settings-notif-actions">
        <button type="button" className="btn btn-primary" onClick={handleSave}>
          {t('settings.saveChanges')}
        </button>
        <button type="button" className="btn btn-secondary settings-notif-disable-everything" onClick={disableEverything}>
          {t('settings.notifDisableEverything')}
        </button>
      </div>
    </div>
  );
}
